import React, { useEffect, useState, useRef } from 'react';
import { Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'vdm_visitor_id';
const POLL_INTERVAL_MS = 20000;

const getOrCreateVisitorId = () => {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `v-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private mode) — generate ephemeral id
    return `v-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
};

const formatNumber = (n) => {
  if (typeof n !== 'number') return '—';
  return n.toLocaleString('en-IN');
};

const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const prev = useRef(value);

  useEffect(() => {
    if (value === prev.current) return;
    const start = prev.current ?? 0;
    const end = value;
    const duration = 800;
    const startedAt = performance.now();

    let frame;
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(start + (end - start) * eased);
      setDisplay(current);
      if (t < 1) frame = requestAnimationFrame(tick);
      else prev.current = end;
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return <span>{formatNumber(display)}</span>;
};

const VisitorCounter = ({ homepageOnly = false, isHomepage = true }) => {
  const [total, setTotal] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (homepageOnly && !isHomepage) return undefined;

    let cancelled = false;
    const controller = new AbortController();

    const track = async () => {
      try {
        const visitorId = getOrCreateVisitorId();
        const res = await fetch(`${BACKEND_URL}/api/visitors/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitor_id: visitorId }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('track failed');
        const data = await res.json();
        if (!cancelled && typeof data.total === 'number') {
          setTotal(data.total);
          setError(false);
        }
      } catch (e) {
        if (!cancelled) setError(true);
      }
    };

    const fetchCount = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/visitors/count`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('count failed');
        const data = await res.json();
        if (!cancelled && typeof data.total === 'number') {
          setTotal(data.total);
          setError(false);
        }
      } catch (e) {
        // keep last known value; only mark error if we never had one
        if (!cancelled && total === null) setError(true);
      }
    };

    track();
    const interval = setInterval(fetchCount, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
    };
    // Effect intentionally bound only to homepage gating; track runs once
  }, [homepageOnly, isHomepage]);

  if (homepageOnly && !isHomepage) return null;

  return (
    <div
      data-testid="visitor-counter"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 18px',
        background: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '999px',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: '#22c55e',
          boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.7)',
          animation: 'vdm-pulse 1.8s ease-out infinite',
        }}
      />
      <Eye size={16} color="var(--brand-primary)" />
      <span
        style={{
          fontSize: '14px',
          color: 'var(--text-muted)',
          letterSpacing: '0.02em',
        }}
      >
        Total Visits:
      </span>
      <span
        data-testid="visitor-counter-value"
        style={{
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
          minWidth: '40px',
          textAlign: 'left',
        }}
      >
        {total === null ? (error ? '—' : '…') : <AnimatedNumber value={total} />}
      </span>
      <style>{`
        @keyframes vdm-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7); }
          70%  { box-shadow: 0 0 0 10px rgba(34, 197, 94, 0); }
          100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
        }
      `}</style>
    </div>
  );
};

export default VisitorCounter;
